import {
  BORROWER_DEFAULTS,
  DEFAULT_TARGET_PRICE,
  HELOC_LOAN_TERMS,
  PPE_FEATURES,
  PROGRAM_FIELD_DEFAULTS,
} from "./config";
import { ARM_TERM_YEARS } from "./armTerms";

// The target price is the Price column: lock-day price plus adjustments,
// compensation not included (the user, 11-04). The calculator caps it at the
// lender's maximum price when both parts are sent. The form has no component
// picker.
const TARGET_PRICE_INCLUDES = ["lockPoint", "adjustment"];
// A target rate the form accepts, in percent.
export const TARGET_RATE_MIN = 0;
export const TARGET_RATE_MAX = 20;
const SECOND_LIEN_PROGRAMS = ["heloc", "secondMortgage"];
// ITIN is one of the form's Citizenship choices (plan D28). The calculator
// reads it as a non-permanent resident with an ITIN.
export const ITIN_CITIZENSHIP = "ITIN";
const ITIN_WIRE_CITIZENSHIP = "NON_PERMANENT_RESIDENT_ALIEN";

const numberOrUndefined = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// The loan terms a form body asks for, in the order picked. Bodies saved
// before the Loan terms multi-select carry only `loanTerm`.
const validTargetRate = (value) => {
  const rate = numberOrUndefined(value);
  return rate !== undefined && rate > TARGET_RATE_MIN && rate < TARGET_RATE_MAX ? rate : undefined;
};

// What a target price is compared with (11-04). "price": the Price column
// (above). "costToBorrower": the rate's Cost to borrower (the calculator's
// closing_cost) on the same base-100 scale, 100 - cost / loan amount x 100,
// so 100 is $0 cost to the borrower and 99 is 1% of the loan amount. The box
// takes the same number either way.
export const TARGET_BASIS_PRICE = "price";
export const TARGET_BASIS_COST = "costToBorrower";

export function targetPriceBasisOf(searchParams) {
  const sp = searchParams || {};
  return sp.targetPriceBasis === TARGET_BASIS_COST ? TARGET_BASIS_COST : TARGET_BASIS_PRICE;
}

// The dollars a cost-to-borrower target price stands for, at a loan amount:
// "≈ $4,000 cost to borrower" for 99 on $400,000, "≈ $2,000 credit to
// borrower" for 100.5. Empty without a price or a loan amount.
export function costToBorrowerHint(targetPrice, loanAmount) {
  const price = numberOrUndefined(targetPrice);
  const amount = numberOrUndefined(loanAmount);
  if (price === undefined || !(amount > 0)) return "";
  const dollars = Math.round((100 - price) * amount / 100);
  const text = "$" + Math.abs(dollars).toLocaleString("en-US");
  return dollars < 0 ? `≈ ${text} credit to borrower` : `≈ ${text} cost to borrower`;
}

// What a form body (or a saved search) targets: { by: "price" | "rate",
// value }, and for a price what it is compared with (basis). A search by rate
// came back on 11-04. Searches saved by rate before it went away (work
// package 5) carry targetRate, and some of the oldest only the rate in
// targetPrice ("targetPrice: 6"); a search by rate with no usable rate prices
// at the default target price.
export function searchTargetOf(searchParams) {
  const sp = searchParams || {};
  const basis = targetPriceBasisOf(sp);
  if (String(sp.searchBy || "").toLowerCase().includes("rate")) {
    const rate = validTargetRate(sp.targetRate) ?? validTargetRate(sp.targetPrice);
    if (rate !== undefined) return { by: "rate", value: rate };
    return { by: "price", value: DEFAULT_TARGET_PRICE, basis };
  }
  return { by: "price", value: numberOrUndefined(sp.targetPrice) ?? DEFAULT_TARGET_PRICE, basis };
}

export function selectedLoanTermsOf(searchParams, loanProgram) {
  const sp = searchParams || {};
  const picked = Array.isArray(sp.selectedLoanTerms) && sp.selectedLoanTerms.length
    ? sp.selectedLoanTerms
    : [sp.loanTerm];
  let terms = [...new Set(picked.filter((term) => typeof term === "string" && term))];
  if (loanProgram === "heloc") {
    terms = terms.filter((term) => HELOC_LOAN_TERMS.includes(term));
    if (!terms.length) terms = ["fixed30"];
  }
  return terms;
}

// Converts the reused Rates search-state shape to the public PPE request.
// The returned wrapper retains legacy display/filter fields, while
// `pricingRequest` is the only object that may be posted to /data/pricing.
//
// LenderDocsProcessors/lender_docs_processors/toDB/ppe_seed.py is a
// line-by-line port of this function for the nightly replay; change both
// (the parity test runs this file under Node against the Python port).
export function formatPpeSearchParams(searchParams) {
  const sp = searchParams || {};
  const n = numberOrUndefined;
  const legacyTypes = (sp.loanTypes || []).map((type) => String(type).toLowerCase());
  let loanProgram = sp.loanProgram;
  if (!loanProgram) {
    if (legacyTypes.some((type) => type.includes("nonqm") || type.includes("non-qm") || type.includes("expanded"))) loanProgram = "nonqm";
    else if (legacyTypes.some((type) => type.includes("fha"))) loanProgram = "fha";
    else if (legacyTypes.some((type) => type.includes("va"))) loanProgram = "va";
    else if (legacyTypes.some((type) => type.includes("usda"))) loanProgram = "usda";
    else loanProgram = "conventional";
  }

  const requestedLien = sp.lienPosition === "second" ? "second" : "first";
  const requestedProgramIsSecondLien = SECOND_LIEN_PROGRAMS.includes(loanProgram);
  const lienPosition = requestedLien === "second" || requestedProgramIsSecondLien
    ? "second"
    : "first";
  if (lienPosition === "second" && !requestedProgramIsSecondLien) {
    loanProgram = "secondMortgage";
  } else if (lienPosition === "first" && requestedProgramIsSecondLien) {
    loanProgram = "conventional";
  }

  const isSecondLienProgram = SECOND_LIEN_PROGRAMS.includes(loanProgram);
  const isHeloc = loanProgram === "heloc";
  const subjectLoanAmount = loanProgram === "heloc"
    ? (n(sp.drawAmount) ?? PROGRAM_FIELD_DEFAULTS.helocDrawAmount)
    : (loanProgram === "secondMortgage"
      ? (n(sp.loanAmount) ?? PROGRAM_FIELD_DEFAULTS.secondMortgageAmount)
      : n(sp.loanAmount));
  const loanTerms = selectedLoanTermsOf(sp, loanProgram);
  // Several terms go out as `loanTerms` only once the calculator takes them
  // (work package 2); until then the form offers one term. Work package 2
  // settles what a multi-term request carries besides the list.
  const multiTerm = PPE_FEATURES.multiTermRequest && loanTerms.length > 1;
  const loanTerm = loanTerms[0];
  const isItin = sp.citizenship === ITIN_CITIZENSHIP;
  const body = {
    lienPosition,
    loanProgram,
    loanPurpose: sp.purpose,
    loanTerm: multiTerm ? undefined : loanTerm,
    loanTerms: multiTerm ? loanTerms : undefined,
    amortizationType: multiTerm
      ? undefined
      : (isHeloc ? "fixed" : (String(loanTerm || "").startsWith("arm") ? "arm" : "fixed")),
    occupancy: sp.occupancy,
    propertyType: sp.property,
    citizenship: isItin ? ITIN_WIRE_CITIZENSHIP : sp.citizenship,
    state: sp.state,
    county: sp.county,
    numberOfUnits: n(sp.unit) || 1,
    baseLoanAmount: subjectLoanAmount,
    propertyValue: n(sp.propertyValue),
    // No FICO (work package 4, D11) sends no score; each lender prices it by
    // its own rule.
    creditScore: PPE_FEATURES.noFico && sp.noFico ? undefined : n(sp.FICO),
    lockDays: n(sp.lockDay) || 30,
    searchBy: "price",
  };
  const setIf = (key, value) => {
    if (value !== undefined) body[key] = value;
  };

  // A non-warrantable condo is the condominium propertyType plus this flag;
  // lenders without non-warrantable condo pricing report the request
  // ineligible instead of pricing a warrantable condo.
  if (sp.nonWarrantableCondo) body.nonWarrantableCondo = true;

  setIf("ltv", n(sp.LTV));
  if (!isSecondLienProgram) {
    setIf("cltv", n(sp.CLTV));
    setIf("hcltv", n(sp.HCLTV));
  }
  if (!isSecondLienProgram) {
    setIf("downPayment", n(sp.downPayment));
    setIf("secondLoanAmount", n(sp.secondaryLoanAmount));
  }
  // Income is optional (plan D3). Without it nothing about income or debt is
  // sent, and the calculator treats the borrower as qualifying on income. An
  // income of 0 means the same: the calculator reads 0 as not given.
  if (n(sp.monthlyIncome) > 0) {
    setIf("monthlyIncome", n(sp.monthlyIncome));
    setIf("monthlyDebt", n(sp.monthlyDebt));
    setIf("dti", n(sp.dti));
  }
  if (loanProgram === "fha") {
    const ufmipPct = n(sp.upFrontMIP);
    if (ufmipPct !== undefined) {
      body.ufmip = Math.ceil((body.baseLoanAmount || 0) * ufmipPct / 100);
    }
  }
  // By price or by rate (searchTargetOf). The calculator orders each
  // product's rates by distance from the target either way; the page picks
  // the Target row itself (resultModel.pickTargetIndex).
  const target = searchTargetOf(sp);
  if (target.by === "rate") {
    body.searchBy = "rate";
    body.targetRate = target.value;
  } else {
    body.targetPrice = target.value;
    body.targetPriceIncludes = [...TARGET_PRICE_INCLUDES];
    // Only when it is not the default, so a search by Price sends what it
    // always sent.
    if (target.basis === TARGET_BASIS_COST) body.targetPriceBasis = TARGET_BASIS_COST;
  }

  // No buydown, even from a saved search that has one (plan D28, D17): no
  // product carries a temporary buydown flag, so any buydown made every
  // product ineligible (Conventional 58 -> 0 on 11-02).
  if (sp.firstTimeHomeBuyer) body.firstTimeHomeBuyer = true;
  if (sp.selfEmployed || sp.isSelfEmployed) body.selfEmployed = true;
  if (sp.isInterestOnly) body.interestOnly = true;
  if (sp.isImpound !== undefined) body.impound = !!sp.isImpound;

  const compensation = String(sp.compensationType || "");
  if (compensation === "lender_paid" || compensation === "lenderPaid") {
    body.compensation = "lenderPaid";
  } else if (compensation === "borrower_paid" || compensation === "borrowerPaid") {
    body.compensation = "borrowerPaid";
    setIf("compensationPercentage", n(sp.compensationPercent));
  }
  if (Array.isArray(sp.ausTypes) && sp.ausTypes.length) {
    body.ausTypes = sp.ausTypes.map((value) => String(value));
  }
  if (body.amortizationType === "arm") {
    setIf("armFixedTerm", ARM_TERM_YEARS[body.loanTerm] || n(sp.armFixedTerm));
  }
  // Entity Vesting (work package 4, D12).
  if (PPE_FEATURES.entityVesting && sp.vesting === "entity") body.vesting = "entity";

  setIf("appraisedValue", n(sp.appraisedValue));
  if (!isSecondLienProgram) setIf("totalLoanAmount", n(sp.totalLoanAmount));
  // The calculator rejects a cash-out amount on any other purpose.
  if (sp.purpose === "CashOutRefi") setIf("cashOutAmount", n(sp.cashOutAmount));
  if (!isSecondLienProgram) {
    setIf("cltv", n(sp.cltvInput));
    setIf("hcltv", n(sp.hcltvInput));
  }
  if (loanProgram === "heloc") {
    setIf("lineAmount", n(sp.lineAmount));
    setIf("drawAmount", n(sp.drawAmount));
    setIf("drawTermMonths", n(sp.drawTermMonths));
  }
  if (isSecondLienProgram) {
    setIf("primaryMortgageAmount", n(sp.primaryMortgageAmount));
    // The backend recomputes second-lien ratios from the amount structure, but
    // secondMortgage's public schema requires CLTV. These values come from the
    // same frontend-derived snapshot used to render the result filters.
    setIf("cltv", n(sp.CLTV));
    setIf("hcltv", n(sp.HCLTV));
  }
  if (loanProgram === "va" && !sp.vaExemptFundingFee) setIf("fundingFee", n(sp.fundingFee));
  if (loanProgram === "va" && sp.vaSubsequentUse) body.vaSubsequentUse = true;
  if (loanProgram === "va" && sp.vaExemptFundingFee) body.vaExemptFundingFee = true;
  if (loanProgram === "usda") setIf("guaranteeFee", n(sp.guaranteeFee));
  setIf("reserves", n(sp.reserves) ?? BORROWER_DEFAULTS.reserves);
  setIf("propertiesFinanced", n(sp.propertiesFinanced) ?? BORROWER_DEFAULTS.propertiesFinanced);
  if (sp.nonOccupantCoBorrower) body.nonOccupantCoBorrower = true;
  // Bodies saved before ITIN joined Citizenship carry a separate itin flag;
  // the calculator applies both the same way.
  if (isItin || sp.itin) body.itin = true;
  if (n(sp.numberOfUnits)) body.numberOfUnits = n(sp.numberOfUnits);
  if (sp.propertyZip) body.propertyZip = String(sp.propertyZip);
  setIf("prepaymentPenalty", n(sp.prepaymentPenalty));
  if (sp.interestOnlyNew) body.interestOnly = true;
  if (sp.nonQm) body.nonQm = sp.nonQm;

  if (loanProgram === "va" && !body.vaExemptFundingFee && body.fundingFee === undefined) {
    body.fundingFee = PROGRAM_FIELD_DEFAULTS.vaFundingFee;
  }
  if (loanProgram === "usda" && body.guaranteeFee === undefined) {
    body.guaranteeFee = PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee;
  }
  if (loanProgram === "heloc" && body.lineAmount === undefined) {
    body.lineAmount = PROGRAM_FIELD_DEFAULTS.helocLineAmount;
  }
  if (loanProgram === "heloc" && body.drawAmount === undefined) {
    body.drawAmount = PROGRAM_FIELD_DEFAULTS.helocDrawAmount;
  }
  if (loanProgram === "heloc" && body.drawTermMonths === undefined) {
    body.drawTermMonths = PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths;
  }
  if (isSecondLienProgram && body.primaryMortgageAmount === undefined) {
    body.primaryMortgageAmount = PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount;
  }
  // A cash-out HELOC has no Cash-out amount on the form (plan D35): the cash
  // is the draw. The calculator still requires cashOutAmount on every
  // cash-out request and does not use it to price a HELOC (checked on
  // production 11-02), so the draw is sent.
  if (isHeloc && sp.purpose === "CashOutRefi") {
    body.cashOutAmount = body.drawAmount;
  }
  Object.keys(body).forEach((key) => {
    if (body[key] === undefined) delete body[key];
  });

  return {
    ...sp,
    ...body,
    // Legacy display field the results area reads: the first term.
    loanTerms: loanTerm,
    loanTerm,
    loanAmount: body.baseLoanAmount,
    pricingRequest: body,
  };
}
